import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { performanceApi } from '../../services/api';
import { getComparator } from '../../utils/tableSort';
import { formatCurrency } from '../../utils/formatters';

const COLUMNS = [
  { id: 'partner_name',              label: 'Partner',      align: 'left'  },
  { id: 'orders_acquired',           label: 'Orders acq.',  align: 'right', format: (v) => (v || 0).toLocaleString() },
  { id: 'revenue_generated',         label: 'Revenue',      align: 'right', format: formatCurrency },
  { id: 'acquisition_spend',         label: 'Acq. spend',   align: 'right', format: formatCurrency },
  { id: 'cpo',                       label: 'CPO',          align: 'right', format: formatCurrency },
  { id: 'orders_sent',               label: 'Orders sent',  align: 'right', format: (v) => (v || 0).toLocaleString() },
  { id: 'affiliate_revenue_earned',  label: 'Aff. earned',  align: 'right', format: formatCurrency },
];

const RetailerOrderDrilldown = ({ open, onClose, retailerId, retailerName, fromDate, toDate }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('orders_acquired');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    if (!open || !retailerId) return;
    setLoading(true);
    setError(null);
    performanceApi
      .getRetailerPartners(retailerId, { from_date: fromDate, to_date: toDate })
      .then((res) => {
        if (res.success) setPartners(res.data.partners || []);
        else setError(res.error || 'Failed to load partner data');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, retailerId, fromDate, toDate]);

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'desc' ? 'asc' : 'desc');
    setOrderBy(col);
  };

  const sorted = [...partners].sort(getComparator(order, orderBy));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Partner breakdown — {retailerName || retailerId}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && !error && sorted.length === 0 && (
          <Typography color="text.secondary">No partner data for this period.</Typography>
        )}
        {!loading && !error && sorted.length > 0 && (
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' }, '& td:not(:first-of-type), & th:not(:first-of-type)': { px: 1 } }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id} align={col.align}>
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'desc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.partner_id} hover>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.id} align={col.align}>
                        {col.format ? col.format(row[col.id]) : row[col.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RetailerOrderDrilldown;
